<template>
  <div class="agent-network">
    <div class="network-toolbar">
      <agent-criticality-legend :agents="nodes" @display-criticality="displayByCriticality"></agent-criticality-legend>
    </div>
    <d3-network
      ref="net"
      :net-nodes="nodes"
      :net-links="links"
      :options="netOptions"
      :sim-cb="networkSimCB"
      @node-click="d3Drag.click"
    />
    <div class="tooltip word-break hidden">
      <agent-info-card :agent="selectedNode" />
    </div>
  </div>
</template>

<script>
import AgentCriticalityLegend from "@/components/legend/AgentCriticalityLegend.vue";
import AgentInfoCard from "@/components/card/AgentInfoCard.vue";

export default {
  name: "D3ForceDirected",
  props: {
    nodes: { type: [Array, Object] },
    links: { type: [Array, Object] },
    options: { type: Object }
  },
  data() {
    return {
      dragging: false,
      selectedNode: {},
      networkSimCB: sim => {
        let height = this.netOptions.size.h;
        let links = this.$refs.net.links;
        function getLinkCount(link) {
          let srcCnt = _.countBy(links, { sourceAgent: link.sid });
          let targetCnt = _.countBy(links, { targetAgent: link.sid });
          return { sourceCount: srcCnt, targetCount: targetCnt };
        }
        sim.force(
          "link",
          this.$d3
            .forceLink(links)
            .id(function(d) {
              return d.id;
            })
            .strength(0.1)
        );
        return sim;
      },
      debounceResize: {}
    };
  },
  computed: {
    netOptions() {
      return _.merge(
        {
          force: 200,
          forces: {
            X: 0.5,
            Y: 0.5,
            ManyBody: true,
            Center: true,
            Link: true
          },
          size: { w: 400, h: 400 },
          nodeSize: 10,
          nodeLabels: false,
          linkLabels: true,
          canvas: false,
          resizeListener: false
        },
        this.options
      );
    },
    d3Slider() {
      return {
        svg: {}
      };
    },
    d3Zoom() {
      let vm = this;
      return {
        svg: {},
        handler: {},
        value: 1,
        step: 0.1, // 10% zoom step
        min: 0.5,
        max: 8,
        formatVal(val) {
          return `${Math.round(val * 100)}%`;
        },
        changeZoom(val) {
          vm.d3Zoom.handler.scaleTo(vm.d3Zoom.svg, this.value);
        }
      };
    },
    d3Drag() {
      let vm = this;
      return {
        svg: {},
        handler: {},
        // Drag functions
        dragStart(d, itemIndex) {
          vm.$d3.event.sourceEvent.stopPropagation();
          let node = vm.$refs.net.nodes[itemIndex];
          if (!node.display) {
            return false;
          }
          vm.$refs.net.dragStart(vm.$d3.event, itemIndex);
        },
        // make sure you can't drag the circle outside the box
        dragging(d, itemIndex) {
          let node = vm.$refs.net.nodes[itemIndex];
          if (!node.display) {
            return false;
          }
          vm.$d3
            .select(this)
            .raise()
            .attr("cx", (node.x = vm.$d3.event.x))
            .attr("cy", (node.y = vm.$d3.event.y));

          node.fx = vm.$d3.event.x;
          node.fy = vm.$d3.event.y;
        },
        dragEnd(d, itemIndex) {
          vm.$refs.net.dragEnd();
        },
        click(d, node) {
          if (!node) {
            return false;
          }
          let routePath = {
            name: "detections",
            query: { hostName: node.customHostname }
          };
          if (vm.$route.name === "dashboard-privileged") {
            routePath.name = "privileged-detections";
          }
          vm.$router.push(routePath);
        }
      };
    }
  },
  methods: {
    unfadeAgents(agents) {
      this.nodes.forEach((node, idx) => {
        if (agents.includes(node.agentId)) {
          this.$set(
            this.nodes,
            node.index,
            _.assignIn(this.nodes[node.index], {
              display: true,
              _cssClass: node._cssClass.replace(/hide/g, "")
            })
          );
          return;
        }
        this.$set(
          this.nodes,
          node.index,
          _.assignIn(this.nodes[node.index], {
            display: false,
            _cssClass: `${node._cssClass} hide`.trim()
          })
        );
      });
    },
    displayAllAgents() {
      this.nodes.forEach((node, idx) => {
        this.$set(
          this.nodes,
          node.index,
          _.assignIn(this.nodes[node.index], {
            display: true,
            _cssClass: node._cssClass.replace(/hide/g, "")
          })
        );
      });
    },
    pinNode(node) {
      node.pinned = true;
      node.fx = node.x;
      node.fy = node.y;
    },
    unpinNode(node) {
      node.pinned = false;
      node.fx = null;
      node.fy = null;
    },
    displayByCriticality(criticality) {
      if (!_.isEmpty(criticality)) {
        this.nodes.forEach((node, idx) => {
          if (node.criticality !== criticality) {
            this.$set(
              this.nodes,
              node.index,
              _.assignIn(this.nodes[node.index], {
                display: false,
                _cssClass: `${node._cssClass} dimmed`.trim()
              })
            );
            return;
          }
          this.$set(
            this.nodes,
            node.index,
            _.assignIn(this.nodes[node.index], {
              display: true,
              _cssClass: node._cssClass.replace(/dimmed/g, "")
            })
          );
        });
      } else {
        this.nodes.forEach(node => {
          if (node._cssClass.includes("dimmed")) {
            this.$set(
              this.nodes,
              node.index,
              _.assignIn(this.nodes[node.index], {
                display: true,
                _cssClass: node._cssClass.replace(/dimmed/g, "")
              })
            );
          }
        });
      }
    },
    nodeMouseOverTooltip(event, node) {
      event.stopPropagation();

      let currNode = event.currentTarget;
      let nodeData = _.find(this.nodes, {
        agentId: currNode.getAttribute("title").replace("node ", "")
      });
      if (nodeData === undefined) {
        return;
      }
      this.showTooltip(event, nodeData);
    },
    showTooltip(el, node, xOffset = 0, yOffset = 0) {
      if (node === undefined) return;
      this.selectedNode = node;

      let tooltipDiv = this.$el.querySelector(".tooltip");
      tooltipDiv.classList.remove("hidden");

      let x, y;

      if (el.pageX && el.pageY) {
        x = el.pageX;
        y = el.pageY;
      } else {
        let elPos = el.getBoundingClientRect();
        x = elPos.x;
        y = elPos.y;
      }

      tooltipDiv.setAttribute(
        "style",
        `left: ${x - 110 - xOffset}px; top: ${y - 150 - yOffset}px;`
      );
    },
    hideTooltip(event, node) {
      event.stopPropagation();
      let tooltipDiv = this.$el.querySelector(".tooltip");
      tooltipDiv.classList.add("hidden");
    },
    setNetDimensions(animate = true) {
      let aspectRatio = 1.25;
      let forceX = 0.5;
      let forceY = 0.5;

      this.netOptions.size.w = `${this.$el.parentElement.clientWidth}`;
      this.netOptions.size.h = `${this.$el.parentElement.clientHeight - 50}`; // give it 100px of offset so nodes don't sit at the border

      let computedAspectRatio = 1;
      // bug: when chart dimensions are less than 5/4 aspect ratio, forceX and Y should be centered (.5, .5)
      if (this.netOptions.size.h > this.netOptions.size.w) {
        computedAspectRatio = this.netOptions.size.h / this.netOptions.size.w;
      } else {
        computedAspectRatio = this.netOptions.size.w / this.netOptions.size.h;
      }

      if (computedAspectRatio >= aspectRatio) {
        forceX = Math.abs(1 - this.netOptions.size.h / this.netOptions.size.w);
        forceY = Math.abs(1 - this.netOptions.size.w / this.netOptions.size.h);
      }

      this.netOptions.forces.X = forceX;
      this.netOptions.forces.Y = forceY;

      // surface area
      let surfaceArea = this.netOptions.size.h * this.netOptions.size.w;
      this.netOptions.force =
        surfaceArea / (this.netOptions.nodeSize * this.nodes.length);

      // check if reference vue-network has been established
      if (!_.isUndefined(this.$refs.net)) {
        this.$set(this.$refs.net, "force", this.netOptions.force);

        if (animate) {
          this.$refs.net.animate();
        }
      }
    }
  },
  mounted() {
    this.setNetDimensions(false);

    // Add mouseover listener to display Agent tooltip
    this.$nextTick(function() {
      let networkEl = this.$el.getElementsByClassName("nodes");
      if (networkEl) {
        let agentNode = networkEl[0].children;
        if (agentNode) {
          for (let i = 0; i < agentNode.length; i++) {
            agentNode[i].addEventListener(
              "mouseover",
              this.nodeMouseOverTooltip
            );
            agentNode[i].addEventListener("mouseout", this.hideTooltip);
          }
        }
      }
    });

    this.debounceResize = _.debounce(this.setNetDimensions, 500);

    // window resize event
    window.addEventListener("resize", this.debounceResize);
  },
  updated: function() {
    // after render, pin/freeze node positions after 2.5s
    this.$nextTick(() => {
      setTimeout(() => {
        if (!_.isEmpty(this.$refs.net)) {
          if (_.isEmpty(this.$el.querySelectorAll(".g-zoom"))) {
            const vm = this;

            const d3 = this.$d3;
            const networkNodes = this.$refs.net;

            let zoomSvg = this.$d3.select(".net-svg").attr("cursor", "grab");
            // get all group elements under the network svg
            let allGroups = zoomSvg.selectAll("g")._groups[0];

            // This element will act as an overlay to make every spot on the network reactive to the zoom event.
            let g = zoomSvg.append("g");
            let overlayRect = g
              .append("rect")
              .attr("class", "overlay")
              .attr("width", this.netOptions.size.w)
              .attr("height", this.netOptions.size.h)
              .style("fill", "none")
              .style("pointer-events", "all");

            // create a new group for all groups
            this.d3Zoom.svg = g
              .append("svg:g")
              .attr("class", "g-zoom")
              .attr("transform", `scale(${this.d3Zoom.value})`);

            // place all groups under a single main group
            allGroups.forEach(group => {
              this.d3Zoom.svg.append(() => {
                return group;
              });
            });

            // apply zoom on main svg:g
            this.d3Zoom.handler = this.$d3
              .zoom()
              .scaleExtent([this.d3Zoom.min, this.d3Zoom.max]) // zoom limit
              .on("zoom", function(d) {
                let eventTransform = vm.$d3.event.transform;
                vm.d3Zoom.svg.attr("transform", eventTransform);
                if (!_.isEmpty(vm.d3Slider.svg)) {
                  vm.d3Zoom.value = eventTransform.k;
                  vm.d3Slider.svg.property("value", eventTransform.k);
                }
              });

            g.call(this.d3Zoom.handler);

            // add drag capabilities
            this.d3Drag.handler = this.$d3
              .drag()
              .on("start", this.d3Drag.dragStart)
              .on("drag", this.d3Drag.dragging)
              .on("end", this.d3Drag.dragEnd);
            let allNodes = zoomSvg.selectAll(".node");
            allNodes.call(this.d3Drag.handler);

            this.d3Slider.svg = this.$d3
              .select(".network-toolbar")
              .append("div")
              .attr("class", "toolbar-slider")
              .append("input")
              .datum({})
              .attr("type", "range")
              .attr("value", this.d3Zoom.value)
              .attr("min", this.d3Zoom.min)
              .attr("max", this.d3Zoom.max)
              .attr(
                "step",
                (this.d3Zoom.handler.scaleExtent()[1] -
                  this.d3Zoom.handler.scaleExtent()[0]) /
                  100
              )
              .on("input", function(d) {
                vm.d3Zoom.handler.scaleTo(g, this.value);
              });
          }
        }
      }, 2500);
    });
  },
  beforeDestroy() {
    // remove event listeners when exiting view
    let networkEl = this.$el.getElementsByClassName("nodes");
    if (networkEl) {
      let agentNode = networkEl[0].children;
      if (agentNode) {
        for (let i = 0; i < agentNode.length; i++) {
          agentNode[i].removeEventListener(
            "mouseover",
            this.nodeMouseOverTooltip
          );
          agentNode[i].removeEventListener("mouseout", this.hideTooltip);
        }
      }
    }

    let zoomSvg = this.$d3.select(".net-svg");
    zoomSvg.selectAll("*").remove();

    window.removeEventListener("resize", this.debounceResize);
  },
  components: { AgentInfoCard, AgentCriticalityLegend }
};
</script>

<style>
@import "assets/styles/_variables.css";

.agent-network {
  .el-slider {
    width: 25%;
    margin-left: 35px;
    display: inline-block;
  }

  .toolbar-slider {
    input {
      appearance: none;
      margin-left: 35px;
      display: inline-block;
      width: 25%;
      height: 5px;
      background: #e4e7ed;
      outline: none;
      opacity: 0.7;
      transition: opacity 0.2s;

      &::-webkit-slider-thumb {
        appearance: none;
        width: 16px;
        height: 16px;
        border: 2px solid #409eff;
        background-color: #fff;
        border-radius: 50%;
        transition: 0.2s;
        user-select: none;
      }

      &::-moz-range-thumb {
        width: 16px;
        height: 16px;
        border: 2px solid #409eff;
        background-color: #fff;
        border-radius: 50%;
        transition: 0.2s;
        user-select: none;
      }
    }
  }

  .agent-legend {
    display: inline-block;
    float: right;
  }

  .net {
    position: relative;

    .node {
      stroke-width: 1;
      stroke: rgba(0, 0, 0, 0.2);
      transition: fill 0.5s ease;

      &.dimmed {
        opacity: 0.2;
      }

      &.hide {
        display: none;
      }
    }
  }

  .tooltip {
    position: fixed;
    width: 100%;
    max-width: 200px;
    text-align: left;
    padding: 10px;
    font-size: 0.8em;
    color: white;
    background: var(--the-menu-bg-color);
    border: 0px;
    border-radius: 8px;
    pointer-events: none;
    opacity: 0.9;
    transition: all 200ms;

    &.hidden {
      opacity: 0;
    }

    .color-block {
      display: none;
    }

    &:after,
    &:before {
      top: 100%;
      left: 50%;
      border: solid transparent;
      content: " ";
      height: 0;
      width: 0;
      position: absolute;
      pointer-events: none;
    }

    &:after {
      border-color: rgba(136, 183, 213, 0);
      border-top-color: var(--the-menu-bg-color);
      border-width: 12px;
      margin-left: -12px;
    }
    &:before {
      border-color: rgba(194, 225, 245, 0);
      border-top-color: var(--the-menu-bg-color);
      border-width: 16px;
      margin-left: -16px;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/network-graph/D3ForceDirected.vue